import { useState } from 'react';
import type { MeResponse } from '@/lib/api';
import { Icon } from '@/components/Icon';
import { haptic, openTelegramLink } from '@/lib/telegram';

interface SupportScreenProps {
  me: MeResponse;
  onInstallOtherDevice: () => void;
}

interface FaqItem {
  q: string;
  a: string;
}

// Static FAQ. The actual fix is "написать в чат" — these are conversation
// starters that match what users open the support bot for. Tapping one
// expands the inline answer; the answer ends with a CTA to message support
// for follow-up. Copy mirrors the bot's /help screen verbatim.
const FAQ: FaqItem[] = [
  {
    q: 'не могу подключиться',
    a: 'проверь, что happ открыт и подписка добавлена. удали и добавь снова через шаг 02 в установке. если не помогло — напиши, посмотрим вместе.',
  },
  {
    q: 'ключ не работает на новом устройстве',
    a: 'одна и та же ссылка работает везде. вставь её в happ на новом устройстве. если happ показывает ошибку — пришли скриншот в чат.',
  },
  {
    q: 'пропала скорость',
    a: 'попробуй переключить сервер в happ (значок земного шара). если медленно во всех серверах — напиши, добавим ближайший.',
  },
  {
    q: 'не пришло уведомление об оплате',
    a: 'оплата приходит на сервер за пару минут. если прошло больше — пришли чек звёзд или хеш ton, активируем вручную.',
  },
];

export function SupportScreen({ me, onInstallOtherDevice }: SupportScreenProps) {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <div
      className="no-scrollbar"
      style={{ padding: '8px 18px 130px', overflowY: 'auto', height: '100%' }}
    >
      <div style={{ marginTop: 8, marginBottom: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          помощь
        </div>
        <h1
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontWeight: 400,
            fontSize: 38,
            lineHeight: 1,
            margin: 0,
            color: 'var(--ink)',
            letterSpacing: '-0.02em',
          }}
        >
          мы рядом.
          <br />
          <span style={{ fontStyle: 'italic', color: 'var(--moss-deep)' }}>каждый день.</span>
        </h1>
        <p
          style={{
            fontSize: 13,
            color: 'var(--ink-2)',
            marginTop: 12,
            lineHeight: 1.5,
            maxWidth: 300,
          }}
        >
          живые люди отвечают днём, ночью и в праздники — обычно за 10 минут
        </p>
      </div>

      {/* primary contact card */}
      <button
        onClick={() => {
          haptic('medium');
          openTelegramLink(me.links.support_bot_url);
        }}
        style={{
          width: '100%',
          background: '#fbf8f1',
          border: '1px solid var(--paper-3)',
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'var(--moss-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="msg" size={20} stroke="var(--moss-deep)" sw={1.7} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>написать в чат</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
            откроется бот поддержки
          </div>
        </div>
        <Icon name="chev" size={14} stroke="var(--ink-3)" sw={1.8} />
      </button>

      <div className="eyebrow" style={{ marginBottom: 10 }}>
        часто спрашивают
      </div>
      <div
        style={{
          background: '#fbf8f1',
          border: '1px solid var(--paper-3)',
          borderRadius: 18,
          overflow: 'hidden',
          marginBottom: 18,
        }}
      >
        {FAQ.map((f, idx) => {
          const isOpen = expanded === idx;
          return (
            <div
              key={f.q}
              style={{
                borderBottom: idx < FAQ.length - 1 ? '1px solid var(--paper-3)' : 'none',
              }}
            >
              <button
                onClick={() => {
                  haptic('light');
                  setExpanded(isOpen ? null : idx);
                }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                }}
                aria-expanded={isOpen}
              >
                <span
                  style={{
                    fontFamily: 'var(--f-mono)',
                    fontSize: 11,
                    color: 'var(--ink-4)',
                    minWidth: 18,
                  }}
                >
                  0{idx + 1}
                </span>
                <span style={{ flex: 1, fontSize: 14, color: 'var(--ink)' }}>{f.q}</span>
                <Icon
                  name="chev"
                  size={14}
                  stroke="var(--ink-4)"
                  sw={1.8}
                  style={{
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 180ms',
                  }}
                />
              </button>
              {isOpen && (
                <div
                  style={{
                    padding: '0 16px 14px 46px',
                    fontSize: 13,
                    color: 'var(--ink-2)',
                    lineHeight: 1.55,
                  }}
                >
                  {f.a}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={() => {
          haptic('light');
          onInstallOtherDevice();
        }}
        className="btn btn-paper"
        style={{ width: '100%', height: 50, fontSize: 13.5 }}
      >
        <Icon name="install" size={16} stroke="var(--ink)" sw={1.6} />
        установка на другом устройстве
      </button>
    </div>
  );
}
